/**
 * Utilidades compartidas para cálculos de honorarios y actualización con índices
 * IMPORTANTE: Esta es la fuente única de verdad para los cálculos
 * Usado por: Dashboard.js, HonorariosMO.js
 */

/**
 * Convierte un monto de una moneda a otra usando el tipo de cambio
 */
export const convertirMonto = (monto, monedaOrigen, monedaDestino, tipoCambio) => {
  if (monedaOrigen === monedaDestino) return monto;
  
  if (monedaOrigen === "USD" && monedaDestino === "ARS") {
    return monto * tipoCambio;
  }
  if (monedaOrigen === "ARS" && monedaDestino === "USD") {
    return monto / tipoCambio;
  }
  
  return monto;
};

/**
 * Formatea una fecha sin timezone para evitar problemas de zona horaria
 */
export const formatearFechaSinTimezone = (fechaString) => {
  if (!fechaString) return "";
  const fecha = new Date(fechaString);
  const year = fecha.getFullYear();
  const month = String(fecha.getMonth() + 1).padStart(2, "0");
  const day = String(fecha.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Formatea una fecha para buscar índices (formato: YYYY-MM-01)
 */
export const formatearFechaParaIndice = (fecha) => {
  if (!fecha) return "";
  const year = fecha.getFullYear();
  const month = String(fecha.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}-01`;
};

/**
 * Agrupa pagos por mes, convirtiendo todos a la misma moneda
 */
export const agruparPagosPorMes = (pagos, monedaDestino) => {
  const pagosPorMes = {};
  
  (pagos || []).forEach((pago) => {
    const fechaPago = new Date(formatearFechaSinTimezone(pago.fecha) + "T00:00:00");
    const mesString = formatearFechaParaIndice(fechaPago);
    
    if (!pagosPorMes[mesString]) {
      pagosPorMes[mesString] = [];
    }
    
    const montoConvertido = convertirMonto(
      pago.monto,
      pago.moneda,
      monedaDestino,
      pago.tipo_cambio
    );
    
    pagosPorMes[mesString].push({
      ...pago,
      montoConvertido,
    });
  });
  
  return pagosPorMes;
};

/**
 * Función especializada para calcular honorarios por porcentaje con actualización por índice
 * Esta función calcula unidades POR CADA GASTO INDIVIDUAL, usando el índice de la fecha del gasto
 * 
 * @param {number} unidadesTotales - Unidades totales pre-calculadas (suma de todas las unidades de cada gasto)
 * @param {Array} gastosConUnidades - Array con cada gasto y sus unidades calculadas
 * @param {string} monedaInicial - Moneda en la que se calculan los honorarios
 * @param {string} metodoActualizacion - Método de actualización (ej: CAC_GENERAL)
 * @param {Array} pagos - Pagos realizados
 * @param {string} tipo - Tipo de cálculo (honorarios, adicionales, mano_obra)
 * @param {Function} obtenerIndicePorFecha - Función para obtener índice por fecha
 * @param {Function} obtenerUltimoIndiceRealDisponible - Función para obtener último índice disponible
 * @param {Object} supabase - Cliente de Supabase
 * @param {string} proyectoId - ID del proyecto
 * @returns {Object} Resultado con evolución y resumen final
 */
export const calcularConSistemaActualizacionPorcentajeAdicionales = async (
  unidadesTotales,
  gastosConUnidades,
  monedaInicial,
  metodoActualizacion,
  pagos,
  tipo,
  obtenerIndicePorFecha,
  obtenerUltimoIndiceRealDisponible,
  supabase,
  proyectoId
) => {
  console.log(`🔄 [SHARED] CALCULANDO PORCENTAJE CON CAC:`, {
    unidadesTotales,
    cantidadGastos: gastosConUnidades.length,
    metodoActualizacion,
    tipo
  });

  // Obtener último índice disponible
  const ultimoIndiceReal = await obtenerUltimoIndiceRealDisponible(
    metodoActualizacion,
    tipo
  );

  if (!ultimoIndiceReal) {
    console.error(`❌ [SHARED] NO SE ENCONTRÓ ÍNDICE para ${metodoActualizacion}`);
    return {
      error: `No se encontró índice para ${metodoActualizacion}.`,
    };
  }

  console.log(`📊 [SHARED] Último índice disponible: ${ultimoIndiceReal}`);

  // Agrupar pagos por mes
  const pagosPorMes = agruparPagosPorMes(pagos, monedaInicial);
  
  // Agrupar gastos por mes
  const gastosPorMes = {};
  gastosConUnidades.forEach((gasto) => {
    const fechaGasto = new Date(formatearFechaSinTimezone(gasto.fecha) + "T00:00:00");
    const mesString = formatearFechaParaIndice(fechaGasto);
    
    if (!gastosPorMes[mesString]) {
      gastosPorMes[mesString] = {
        gastos: [],
        totalGastoBruto: 0,
        porcentaje: gasto.porcentajeAplicado || 0,
        totalGastado: 0,
        totalUnidades: 0,
        cantidadGastos: 0
      };
    }
    
    gastosPorMes[mesString].gastos.push(gasto);
    gastosPorMes[mesString].totalGastoBruto += gasto.montoConvertido || 0;
    gastosPorMes[mesString].totalGastado += gasto.honorarioCalculado || 0;
    gastosPorMes[mesString].totalUnidades += gasto.unidades || 0;
    gastosPorMes[mesString].cantidadGastos++;
  });
  
  // Procesar evolución mes a mes
  const evolucion = [];
  let unidadesPagadas = 0;
  const fechaHoy = new Date();
  
  // Obtener el mes más antiguo
  const fechasGastos = gastosConUnidades.map(g => new Date(formatearFechaSinTimezone(g.fecha) + "T00:00:00"));
  const fechaMasAntigua = fechasGastos.length > 0 ? new Date(Math.min(...fechasGastos)) : new Date();
  let mesActual = new Date(fechaMasAntigua.getFullYear(), fechaMasAntigua.getMonth(), 1);

  console.log(`📅 [SHARED] Procesando desde ${formatearFechaParaIndice(mesActual)} hasta ${formatearFechaParaIndice(fechaHoy)}`);

  while (mesActual <= fechaHoy) {
    const mesString = formatearFechaParaIndice(mesActual);
    const pagosDelMes = pagosPorMes[mesString] || [];
    const gastosDelMes = gastosPorMes[mesString] || { 
      gastos: [], 
      totalGastoBruto: 0, 
      porcentaje: 0, 
      totalGastado: 0, 
      totalUnidades: 0, 
      cantidadGastos: 0 
    };

    // Índice para este mes
    let indiceDelMes = await obtenerIndicePorFecha(
      mesString,
      metodoActualizacion,
      tipo
    );
    let esProvisorio = false;
    let esEditado = false;

    // Verificar si hay valor personalizado
    if (supabase && proyectoId) {
      try {
        const { data: valorPersonalizado } = await supabase
          .from("valores_personalizados_actualizacion")
          .select("coeficiente")
          .eq("proyecto_id", proyectoId)
          .eq("tipo", tipo)
          .eq("fecha_aplicacion", mesString)
          .maybeSingle();

        if (valorPersonalizado) {
          esEditado = true;
        }
      } catch (err) {
        // Ignorar error
      }
    }

    if (!indiceDelMes) {
      indiceDelMes = ultimoIndiceReal;
      esProvisorio = true;
    }

    if (!indiceDelMes) {
      console.error(`❌ [SHARED] NO SE ENCONTRÓ ÍNDICE para ${mesString}`);
      break;
    }

    // Convertir pagos del mes a unidades
    let unidadesDelMes = 0;
    const pagosDetallados = pagosDelMes.map((pago) => {
      const unidadesPago = pago.montoConvertido / indiceDelMes;
      unidadesDelMes += unidadesPago;
      return {
        ...pago,
        indiceUsado: indiceDelMes,
        unidades: unidadesPago,
        esProvisorio: esProvisorio,
        esEditado: esEditado,
      };
    });

    unidadesPagadas += unidadesDelMes;

    // Saldo en unidades y en pesos
    const unidadesRestantes = unidadesTotales - unidadesPagadas;
    const saldoEnPesos = unidadesRestantes * indiceDelMes;

    evolucion.push({
      mes: mesString,
      fecha: mesString,
      indiceUsado: indiceDelMes,
      esProvisorio: esProvisorio,
      esEditado: esEditado,
      unidadesDelMes: unidadesDelMes,
      unidadesGastadas: unidadesPagadas,
      unidadesRestantes: unidadesRestantes,
      saldoEnPesos: saldoEnPesos,
      pagosDelMes: pagosDetallados,
      cantidadPagos: pagosDelMes.length,
      // Datos de gastos del mes
      gastosDelMes: gastosDelMes.gastos,
      totalGastoBrutoMes: gastosDelMes.totalGastoBruto,
      porcentajeGastos: gastosDelMes.porcentaje,
      totalGastadoMes: gastosDelMes.totalGastado,
      unidadesGastosMes: gastosDelMes.totalUnidades,
      cantidadGastos: gastosDelMes.cantidadGastos,
      observaciones: esEditado 
        ? `Índice personalizado/editado`
        : esProvisorio
        ? `Índice provisorio (último disponible)`
        : `Índice oficial del mes`,
    });

    mesActual.setMonth(mesActual.getMonth() + 1);
  }

  // Calcular totales finales
  const unidadesFinales = unidadesTotales - unidadesPagadas;
  const saldoFinalEnPesos = unidadesFinales * ultimoIndiceReal;
  const totalPagadoEnPesos = (pagos || []).reduce((sum, pago) => {
    return sum + convertirMonto(pago.monto, pago.moneda, monedaInicial, pago.tipo_cambio);
  }, 0);

  console.log(`💰 [SHARED] RESULTADO FINAL:`, {
    unidadesTotales: Math.round(unidadesTotales),
    unidadesPagadas: Math.round(unidadesPagadas),
    unidadesRestantes: Math.round(unidadesFinales),
    ultimoIndice: Math.round(ultimoIndiceReal),
    saldoFinalEnPesos: Math.round(saldoFinalEnPesos),
  });

  return {
    unidadesSistema: unidadesTotales,
    ultimoIndiceDisponible: ultimoIndiceReal,
    evolucion: evolucion,
    resumenFinal: {
      montoActualizado: unidadesFinales * ultimoIndiceReal,
      pagosAcumulados: totalPagadoEnPesos,
      saldoPendiente: saldoFinalEnPesos,
      unidades: unidadesFinales,
      ultimoIndiceUsado: ultimoIndiceReal,
      totalUnidades: unidadesTotales,
      unidadesGastadas: unidadesPagadas,
    },
  };
};

